import { ref, shallowRef, computed } from 'vue';
import { isDocumentResult } from '@/utils/documentResultValidator';

export function useDocumentSelection(executionResults) {
  const self = {
    selectedDocumentItems: shallowRef(new Map()),
    selectedResultRows: shallowRef(new Set()),
    expandedDocumentItems: ref([]),
  };

  self.getSelectedDocumentItems = (rowId) => {
    const key = String(rowId);
    if (!self.selectedDocumentItems.value.has(key)) {
      self.selectedDocumentItems.value.set(key, new Set());
    }
    return self.selectedDocumentItems.value.get(key);
  };

  self.toggleDocumentItem = (rowId, itemIndex) => {
    const items = self.getSelectedDocumentItems(rowId);

    if (items.has(itemIndex)) {
      items.delete(itemIndex);
    } else {
      items.add(itemIndex);
    }

    self.selectedDocumentItems.value = new Map(self.selectedDocumentItems.value);

    const result = executionResults.value.find(r => r.rowId === rowId);
    if (result && isDocumentResult(result)) {
      const newRows = new Set(self.selectedResultRows.value);
      if (items.size > 0) {
        newRows.add(rowId);
      } else {
        newRows.delete(rowId);
      }
      self.selectedResultRows.value = newRows;
    }
  };

  function updateDocumentItemSelection(config) {
    const {
      resultRowId = null,
      allRows = false,
      action = 'select',
      documentResult = null,
      getComponentRef = null
    } = config;

    const rowIds = allRows
      ? executionResults.value
          .filter(r => isDocumentResult(r))
          .map(r => r.rowId)
      : [resultRowId];

    const newRows = new Set(self.selectedResultRows.value);

    rowIds.forEach(rowId => {
      const items = self.getSelectedDocumentItems(rowId);
      const componentRef = getComponentRef ? getComponentRef(rowId) : null;

      if (action === 'select') {
        items.clear();

        const populatedData = allRows
          ? executionResults.value.find(r => r.rowId === rowId)?.result?.populatedData || []
          : documentResult?.populatedData || [];

        populatedData.forEach((_, index) => {
          items.add(index);
        });

        componentRef?.selectAll();
        newRows.add(rowId);
      } else if (action === 'clear') {
        items.clear();
        componentRef?.clearAll();
        newRows.delete(rowId);
      }
    });

    self.selectedResultRows.value = newRows;
    self.selectedDocumentItems.value = new Map(self.selectedDocumentItems.value);
  }

  self.selectAllDocumentItems = (rowId, documentResult, getComponentRef) => {
    updateDocumentItemSelection({
      resultRowId: rowId,
      action: 'select',
      documentResult,
      getComponentRef
    });
  };

  self.clearAllDocumentItems = (rowId, getComponentRef) => {
    updateDocumentItemSelection({
      resultRowId: rowId,
      action: 'clear',
      getComponentRef
    });
  };

  self.selectAllInAllRows = (getComponentRef) => {
    updateDocumentItemSelection({
      allRows: true,
      action: 'select',
      getComponentRef
    });
  };

  self.clearAllInAllRows = (getComponentRef) => {
    updateDocumentItemSelection({
      allRows: true,
      action: 'clear',
      getComponentRef
    });
  };

  self.isDocumentItemSelected = (rowId, itemIndex) => {
    return self.getSelectedDocumentItems(rowId).has(itemIndex);
  };

  self.getSelectedDocumentCount = (rowId) => {
    return self.getSelectedDocumentItems(rowId).size;
  };

  self.getRowCheckboxState = (resultRowId) => {
    const result = executionResults.value.find(r => r.rowId === resultRowId);
    if (!result || !isDocumentResult(result)) {
      return { checked: false, indeterminate: false };
    }

    const selectedCount = self.getSelectedDocumentCount(resultRowId);
    const totalCount = result.result.populatedData.length;

    if (selectedCount === 0) {
      return { checked: false, indeterminate: false };
    } else if (selectedCount === totalCount) {
      return { checked: true, indeterminate: false };
    } else {
      return { checked: false, indeterminate: true };
    }
  };

  self.handleRowCheckboxClick = (resultRowId, getComponentRef) => {
    const result = executionResults.value.find(r => r.rowId === resultRowId);
    if (!result || !isDocumentResult(result)) return;

    const selectedCount = self.getSelectedDocumentCount(resultRowId);
    const totalCount = result.result.populatedData.length;

    if (selectedCount === totalCount) {
      self.clearAllDocumentItems(resultRowId, getComponentRef);
    } else {
      self.selectAllDocumentItems(resultRowId, result.result, getComponentRef);
    }
  };

  self.handleDocumentSelectionChanged = (resultRowId, data) => {
    const key = String(resultRowId);
    const items = new Set(data.selectedIndices);
    self.selectedDocumentItems.value.set(key, items);

    self.selectedDocumentItems.value = new Map(self.selectedDocumentItems.value);

    const result = executionResults.value.find(r => r.rowId === resultRowId);
    if (result && isDocumentResult(result)) {
      const newRows = new Set(self.selectedResultRows.value);
      if (items.size > 0) {
        newRows.add(resultRowId);
      } else {
        newRows.delete(resultRowId);
      }
      self.selectedResultRows.value = newRows;
    }
  };

  self.clearAllResultRowSelections = () => {
    self.selectedResultRows.value = new Set();
  };

  self.expandAllDocumentItems = (rowId, documentResult, getComponentRef) => {
    const documentItems = documentResult.populatedData || [];
    const allIndices = documentItems.map((_, index) => index);
    self.expandedDocumentItems.value = allIndices;

    const componentRef = getComponentRef ? getComponentRef(rowId) : null;
    componentRef?.expandAll();
  };

  self.collapseAllDocumentItems = (rowId, getComponentRef) => {
    self.expandedDocumentItems.value = [];

    const componentRef = getComponentRef ? getComponentRef(rowId) : null;
    componentRef?.collapseAll();
  };

  self.hasAnySelectedItems = computed(() => {
    return executionResults.value.some(result =>
      isDocumentResult(result) && self.getSelectedDocumentCount(result.rowId) > 0
    );
  });

  self.totalSelectedItemsAllRows = computed(() => {
    let total = 0;
    for (const result of executionResults.value) {
      if (isDocumentResult(result)) {
        total += self.getSelectedDocumentCount(result.rowId);
      }
    }
    return total;
  });

  return self;
}
